import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = {
  navy:'#0D1B2A', teal:'#00B4D8', grey:'#6B7280', border:'#E5E7EB',
  bg:'#F0F7FA', red:'#EF4444', green:'#10B981', orange:'#F59E0B', purple:'#8B5CF6',
};

const LEVELS  = ['A1','A2','B1','B2','C1','C2'];
const SKILLS  = ['grammar','vocabulary','writing','reading','speaking'];
const SKILL_COLORS = {
  grammar:    { bg:'#EFF6FF', color:'#1D4ED8' },
  vocabulary: { bg:'#F0FDF4', color:'#16A34A' },
  writing:    { bg:'#FFF7ED', color:'#C2410C' },
  reading:    { bg:'#F5F3FF', color:'#7C3AED' },
  speaking:   { bg:'#FEF9C3', color:'#92400E' },
};

function SkillBadge({ skill }) {
  const s = SKILL_COLORS[skill] || { bg:'#F3F4F6', color:'#374151' };
  return (
    <span style={{ background:s.bg, color:s.color, fontSize:11, fontWeight:600,
      padding:'2px 9px', borderRadius:20 }}>
      {skill}
    </span>
  );
}

function LevelBadge({ level }) {
  return (
    <span style={{ background:'#0D1B2A', color:'#C9A84C', fontSize:11, fontWeight:700,
      padding:'2px 9px', borderRadius:20, letterSpacing:1 }}>
      {level}
    </span>
  );
}

function MaterialDetailModal({ material, onClose, onToggle }) {
  if (!material) return null;
  const content = material.content || {};
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:620, maxHeight:'88vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16, gap:12 }}>
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:8, flexWrap:'wrap' }}>
              <LevelBadge level={material.cefr_level} />
              <SkillBadge skill={material.skill_type} />
              {material.theme && (
                <span style={{ background:'#F3F4F6', color:'#374151', fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:20 }}>
                  {material.theme}
                </span>
              )}
            </div>
            <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:C.navy }}>{material.title}</h2>
            {material.description && <p style={{ margin:'6px 0 0', fontSize:13, color:C.grey }}>{material.description}</p>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.grey, flexShrink:0 }}>✕</button>
        </div>

        <div style={{ background:'#F9FAFB', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:12, color:C.grey }}>
          <span style={{ fontWeight:600 }}>Topic tag:</span> <code style={{ background:'#E5E7EB', borderRadius:4, padding:'1px 6px' }}>{material.topic_tag}</code>
          &nbsp;·&nbsp;<span style={{ fontWeight:600 }}>Used in sessions:</span> {material.usage_count || 0}×
        </div>

        {/* Grammar: explanation + examples + exercises */}
        {content.explanation && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:6 }}>📖 Explanation</div>
            <div style={{ fontSize:13, color:'#374151', lineHeight:1.6, background:'#F0F7FA', borderRadius:8, padding:'10px 14px' }}>{content.explanation}</div>
          </div>
        )}
        {content.examples && content.examples.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:6 }}>✏️ Examples</div>
            <ul style={{ margin:0, paddingLeft:18 }}>
              {content.examples.map((ex, i) => <li key={i} style={{ fontSize:13, color:'#374151', marginBottom:4, lineHeight:1.5 }}>{ex}</li>)}
            </ul>
          </div>
        )}
        {/* Vocabulary: words */}
        {content.words && content.words.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:8 }}>📚 Words</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {content.words.map((w, i) => (
                <div key={i} style={{ background:'#F9FAFB', borderRadius:8, padding:'8px 12px' }}>
                  <span style={{ fontWeight:700, fontSize:13 }}>{w.word}</span>
                  <span style={{ fontSize:12, color:C.grey, marginLeft:8 }}>— {w.definition}</span>
                  {w.example && <div style={{ fontSize:12, color:'#6B7280', fontStyle:'italic', marginTop:3 }}>"{w.example}"</div>}
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Writing: prompt */}
        {content.prompt && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:6 }}>✍️ Writing Prompt</div>
            <div style={{ fontSize:13, color:'#374151', background:'#FFF7ED', borderRadius:8, padding:'10px 14px', lineHeight:1.6 }}>{content.prompt}</div>
            {content.requirements && (
              <ul style={{ margin:'8px 0 0', paddingLeft:18 }}>
                {content.requirements.map((r, i) => <li key={i} style={{ fontSize:12, color:C.grey, marginBottom:3 }}>{r}</li>)}
              </ul>
            )}
          </div>
        )}
        {/* Reading: passage */}
        {content.passage && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:6 }}>📄 Reading Passage</div>
            <div style={{ fontSize:13, color:'#374151', background:'#F5F3FF', borderRadius:8, padding:'10px 14px', lineHeight:1.7 }}>{content.passage}</div>
          </div>
        )}
        {/* Exercises */}
        {content.exercises && content.exercises.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.navy, marginBottom:8 }}>🧩 Exercises ({content.exercises.length})</div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {content.exercises.slice(0,5).map((ex, i) => (
                <div key={i} style={{ background:'#F9FAFB', borderRadius:8, padding:'8px 12px', fontSize:12 }}>
                  <span style={{ fontWeight:600, color:C.navy }}>{i+1}. </span>{ex.question}
                  {ex.answer && <span style={{ color:C.green, marginLeft:8, fontWeight:600 }}>✓ {ex.answer}</span>}
                </div>
              ))}
              {content.exercises.length > 5 && <div style={{ fontSize:11, color:C.grey, textAlign:'center' }}>+{content.exercises.length-5} more exercises</div>}
            </div>
          </div>
        )}

        {material.file_url && (
          <div style={{ background:'#F0F7FA', borderRadius:10, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:22 }}>{material.file_type === 'pdf' ? '📄' : material.file_type?.includes('ppt') ? '📊' : '📝'}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.navy }}>Attached file</div>
              <div style={{ fontSize:12, color:C.grey, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{material.file_name}</div>
            </div>
            <a href={`${process.env.REACT_APP_API_URL || ''}${material.file_url}`}
              target="_blank" rel="noopener noreferrer"
              style={{ padding:'7px 16px', background:C.teal, color:'#fff', borderRadius:8, fontWeight:700, fontSize:12, textDecoration:'none', whiteSpace:'nowrap' }}>
              Open file
            </a>
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'space-between', marginTop:20 }}>
          <button onClick={() => { onToggle(material); onClose(); }}
            style={{ padding:'9px 18px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13, color: material.is_active ? C.red : C.green, fontWeight:600 }}>
            {material.is_active ? 'Hide Material' : 'Show Material'}
          </button>
          <button onClick={onClose}
            style={{ padding:'9px 22px', background:C.navy, color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function AddMaterialModal({ onClose, onSaved }) {
  const [tab, setTab]   = useState('manual'); // 'manual' | 'upload'
  const [form, setForm] = useState({
    cefr_level:'B1', skill_type:'grammar', topic_tag:'', title:'', description:'', theme:''
  });
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  // Upload tab state
  const [file, setFile]       = useState(null);
  const [upForm, setUpForm]   = useState({ cefr_level:'B1', skill_type:'grammar', title:'', description:'', theme:'' });
  const [dragging, setDragging] = useState(false);

  const save = async () => {
    if (!form.topic_tag.trim() || !form.title.trim()) { setErr('Title and Topic Tag are required'); return; }
    setSaving(true); setErr('');
    try {
      const r = await api.post('/api/admin/lesson-materials', form);
      onSaved(r.data.material);
      onClose();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const uploadFile = async () => {
    if (!file) { setErr('Please select a file'); return; }
    if (!upForm.title.trim()) { setErr('Title is required'); return; }
    setSaving(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', upForm.title);
      fd.append('cefr_level', upForm.cefr_level);
      fd.append('skill_type', upForm.skill_type);
      fd.append('description', upForm.description);
      fd.append('theme', upForm.theme);
      fd.append('topic_tag', upForm.title.toLowerCase().replace(/[^a-z0-9]+/g,'_').slice(0,60));
      const r = await api.post('/api/admin/lesson-materials/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSaved(r.data.material);
      onClose();
    } catch (e) { setErr(e.response?.data?.error || 'Upload failed'); }
    finally { setSaving(false); }
  };

  const field = (label, key, src, setSrc, props = {}) => (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.navy, marginBottom:5 }}>{label}</label>
      <input value={src[key]} onChange={e => setSrc(f => ({...f, [key]:e.target.value}))}
        style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, boxSizing:'border-box' }}
        {...props} />
    </div>
  );

  const selects = (src, setSrc) => (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
      <div>
        <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.navy, marginBottom:5 }}>CEFR Level</label>
        <select value={src.cefr_level} onChange={e => setSrc(f => ({...f, cefr_level:e.target.value}))}
          style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13 }}>
          {LEVELS.map(l => <option key={l}>{l}</option>)}
        </select>
      </div>
      <div>
        <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.navy, marginBottom:5 }}>Skill</label>
        <select value={src.skill_type} onChange={e => setSrc(f => ({...f, skill_type:e.target.value}))}
          style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13 }}>
          {SKILLS.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );

  const handleDrop = e => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); setUpForm(p => ({ ...p, title: p.title || f.name.replace(/\.[^.]+$/, '') })); }
  };

  const EXT_ICON = { pdf:'📄', doc:'📝', docx:'📝', ppt:'📊', pptx:'📊', xlsx:'📋', xls:'📋' };
  const fileExt  = file ? file.name.split('.').pop().toLowerCase() : '';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:500, maxHeight:'90vh', overflowY:'auto' }}>
        <h3 style={{ margin:'0 0 20px', fontSize:16, fontWeight:700, color:C.navy }}>Add Lesson Material</h3>

        {/* Tab switcher */}
        <div style={{ display:'flex', gap:0, marginBottom:22, border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
          {[['manual','✏️ Create manually'],['upload','📎 Upload file']].map(([t,label]) => (
            <button key={t} onClick={() => { setTab(t); setErr(''); }}
              style={{ flex:1, padding:'9px 0', border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
                background: tab === t ? C.navy : '#fff', color: tab === t ? '#fff' : C.grey }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'manual' ? (
          <>
            {selects(form, setForm)}
            {field('Topic Tag (slug)', 'topic_tag', form, setForm, { placeholder:'e.g. present_perfect' })}
            {field('Title', 'title', form, setForm, { placeholder:'e.g. The Present Perfect Tense' })}
            {field('Theme (optional)', 'theme', form, setForm, { placeholder:'e.g. Work & Career' })}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.navy, marginBottom:5 }}>Description (optional)</label>
              <textarea value={form.description} onChange={e => setForm(f => ({...f, description:e.target.value}))}
                rows={3} placeholder="What this material covers…"
                style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }} />
            </div>
          </>
        ) : (
          <>
            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => document.getElementById('lm-file-input').click()}
              style={{ border:`2px dashed ${dragging ? C.teal : '#D1D5DB'}`, borderRadius:12, padding:'28px 20px',
                textAlign:'center', cursor:'pointer', marginBottom:18, background: dragging ? '#F0F7FA' : '#FAFAFA',
                transition:'all 0.15s' }}>
              <input id="lm-file-input" type="file" style={{ display:'none' }}
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xlsx,.xls"
                onChange={e => { const f = e.target.files[0]; if (f) { setFile(f); setUpForm(p => ({ ...p, title: p.title || f.name.replace(/\.[^.]+$/, '') })); } }} />
              {file ? (
                <div>
                  <div style={{ fontSize:32, marginBottom:6 }}>{EXT_ICON[fileExt] || '📎'}</div>
                  <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>{file.name}</div>
                  <div style={{ fontSize:12, color:C.grey, marginTop:3 }}>{(file.size/1024).toFixed(0)} KB · click to change</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:32, marginBottom:8 }}>📎</div>
                  <div style={{ fontWeight:600, fontSize:14, color:C.navy }}>Drop file here or click to browse</div>
                  <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>PDF, Word (.docx), PowerPoint (.pptx), Excel</div>
                </div>
              )}
            </div>
            {selects(upForm, setUpForm)}
            {field('Title', 'title', upForm, setUpForm, { placeholder:'e.g. Present Perfect Worksheet' })}
            {field('Theme (optional)', 'theme', upForm, setUpForm, { placeholder:'e.g. Work & Career' })}
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:12, fontWeight:600, color:C.navy, marginBottom:5 }}>Description (optional)</label>
              <textarea value={upForm.description} onChange={e => setUpForm(f => ({...f, description:e.target.value}))}
                rows={2} placeholder="Brief description for tutors…"
                style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }} />
            </div>
          </>
        )}

        {err && <div style={{ color:C.red, fontSize:12, marginBottom:12 }}>{err}</div>}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'9px 18px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>Cancel</button>
          <button onClick={tab === 'manual' ? save : uploadFile} disabled={saving}
            style={{ padding:'9px 20px', background:C.navy, color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer', fontSize:13, opacity:saving?0.7:1 }}>
            {saving ? (tab === 'upload' ? 'Uploading…' : 'Saving…') : (tab === 'upload' ? 'Upload & Save' : 'Add Material')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminLessonBank() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterLevel) params.level = filterLevel;
      if (filterSkill) params.skill = filterSkill;
      const r = await api.get('/api/admin/lesson-materials', { params });
      setMaterials(r.data.materials || []);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterLevel, filterSkill]); // eslint-disable-line

  const toggleActive = async (m) => {
    try {
      await api.put(`/api/admin/lesson-materials/${m.id}`, { is_active: !m.is_active });
      setMaterials(prev => prev.map(x => x.id === m.id ? {...x, is_active:!x.is_active} : x));
      if (selectedMaterial?.id === m.id) setSelectedMaterial(s => ({...s, is_active:!s.is_active}));
    } catch { alert('Failed to update'); }
  };

  const filtered = materials.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return m.title.toLowerCase().includes(q) || (m.topic_tag||'').toLowerCase().includes(q) || (m.theme||'').toLowerCase().includes(q);
  });

  // Stats
  const byLevel = LEVELS.map(l => ({ level:l, count: materials.filter(m => m.cefr_level === l).length }));
  const bySkill = SKILLS.map(s => ({ skill:s, count: materials.filter(m => m.skill_type === s).length }));
  const totalUsage = materials.reduce((sum, m) => sum + (m.usage_count||0), 0);

  return (
    <Layout>
      <div style={{ padding:'28px 32px', maxWidth:1100, margin:'0 auto' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ margin:0, fontSize:22, fontWeight:800, color:C.navy }}>📚 Lesson Material Bank</h1>
            <p style={{ margin:'4px 0 0', color:C.grey, fontSize:14 }}>CEFR-aligned materials tutors use in sessions — AI practice reinforces these topics</p>
          </div>
          <button onClick={() => setShowAdd(true)}
            style={{ padding:'10px 20px', background:C.navy, color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14 }}>
            + Add Material
          </button>
        </div>

        {/* Stats row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:24 }}>
          <div style={{ background:'#fff', border:'1.5px solid #E5E7EB', borderRadius:12, padding:'14px 18px' }}>
            <div style={{ fontSize:24, fontWeight:800, color:C.navy }}>{materials.length}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>Total materials</div>
          </div>
          <div style={{ background:'#fff', border:'1.5px solid #E5E7EB', borderRadius:12, padding:'14px 18px' }}>
            <div style={{ fontSize:24, fontWeight:800, color:C.teal }}>{totalUsage}</div>
            <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>Times used in sessions</div>
          </div>
          {bySkill.map(s => (
            <div key={s.skill} style={{ background:'#fff', border:'1.5px solid #E5E7EB', borderRadius:12, padding:'14px 18px' }}>
              <div style={{ fontSize:20, fontWeight:800, color:SKILL_COLORS[s.skill]?.color || C.navy }}>{s.count}</div>
              <div style={{ fontSize:12, color:C.grey, marginTop:2, textTransform:'capitalize' }}>{s.skill}</div>
            </div>
          ))}
        </div>

        {/* Level bar */}
        <div style={{ background:'#fff', border:'1.5px solid #E5E7EB', borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:600, color:C.navy, marginBottom:10 }}>Materials by CEFR Level</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {byLevel.map(l => (
              <div key={l.level} style={{ flex:1, minWidth:60, textAlign:'center' }}>
                <div style={{ fontSize:18, fontWeight:800, color:C.navy }}>{l.count}</div>
                <div style={{ fontSize:11, color:C.grey }}>{l.level}</div>
                <div style={{ height:4, background:'#E5E7EB', borderRadius:4, marginTop:4, overflow:'hidden' }}>
                  <div style={{ width:`${materials.length ? (l.count/materials.length)*100 : 0}%`, height:'100%', background:C.teal, borderRadius:4 }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          <input placeholder="Search materials…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex:1, minWidth:160, border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 14px', fontSize:13 }} />
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
            style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 12px', fontSize:13, background:'#fff' }}>
            <option value="">All levels</option>
            {LEVELS.map(l => <option key={l}>{l}</option>)}
          </select>
          <select value={filterSkill} onChange={e => setFilterSkill(e.target.value)}
            style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 12px', fontSize:13, background:'#fff' }}>
            <option value="">All skills</option>
            {SKILLS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
        </div>

        {/* Materials table */}
        {loading ? (
          <div style={{ textAlign:'center', padding:40, color:C.grey }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:40, color:C.grey, background:'#fff', borderRadius:12, border:'1.5px solid #E5E7EB' }}>
            No materials found
          </div>
        ) : (
          <div style={{ background:'#fff', border:'1.5px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #E5E7EB', background:'#F9FAFB' }}>
                  {['Level','Skill','Title','Theme','Used','Status',''].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'10px 14px', fontSize:11, fontWeight:700, color:C.grey, textTransform:'uppercase', letterSpacing:0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, i) => (
                  <tr key={m.id}
                    onClick={() => setSelectedMaterial(m)}
                    style={{
                      borderBottom: i < filtered.length-1 ? '1px solid #F3F4F6' : 'none',
                      opacity: m.is_active ? 1 : 0.5,
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <td style={{ padding:'10px 14px' }}><LevelBadge level={m.cefr_level} /></td>
                    <td style={{ padding:'10px 14px' }}><SkillBadge skill={m.skill_type} /></td>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ fontSize:13, fontWeight:600, color:C.navy }}>{m.file_url && <span title="File attached" style={{ marginRight:4 }}>📎</span>}{m.title}</div>
                      {m.description && <div style={{ fontSize:11, color:C.grey, marginTop:2 }}>{m.description.slice(0,70)}{m.description.length>70?'…':''}</div>}
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:12, color:C.grey }}>{m.theme || '—'}</td>
                    <td style={{ padding:'10px 14px', fontSize:13, color:m.usage_count > 0 ? C.green : C.grey, fontWeight: m.usage_count > 0 ? 700 : 400 }}>
                      {m.usage_count || 0}×
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:20,
                        background: m.is_active ? '#D1FAE5' : '#FEE2E2',
                        color: m.is_active ? '#16A34A' : '#DC2626' }}>
                        {m.is_active ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <button onClick={e => { e.stopPropagation(); toggleActive(m); }}
                        style={{ fontSize:11, padding:'4px 10px', border:'1px solid #E5E7EB', borderRadius:7, background:'#fff', cursor:'pointer', color:C.grey }}>
                        {m.is_active ? 'Hide' : 'Show'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <AddMaterialModal
          onClose={() => setShowAdd(false)}
          onSaved={m => { setMaterials(prev => [m, ...prev]); }}
        />
      )}

      {selectedMaterial && (
        <MaterialDetailModal
          material={selectedMaterial}
          onClose={() => setSelectedMaterial(null)}
          onToggle={toggleActive}
        />
      )}
    </Layout>
  );
}
